import { PermissionEnum } from '@sr-sdks/api-sdk-axios';
import { lazy } from 'react';
import {
  type RouteObject,
  createBrowserRouter,
  redirect,
} from 'react-router-dom';
import { prefetchWorkspace, prefetchWorkspaces } from '../api/prefetch';
import ErrorPage from '../components/ErrorPage';
import { MigrationSwitch } from '../components/MigrationSwitch';
import { NavigateWithParams } from '../components/NavigateWithParams';
import { WorkspaceRedirect } from '../components/WorkspaceRedirect';
import { configuration } from '../configuration';
import { MSR3_HOME_FLAGS } from '../constant/featureFlags';
import DashboardLayout from '../layout/DashboardLayout';
import { MSR3BaseLayout } from '../layout/MSR3BaseLayout';
import RootLayout from '../layout/RootLayout';
import Dashboard from '../pages/Dashboard/Dashboard';
import { AdminRoutes } from './AdminRoutes';
import { EmbedRoutes } from './EmbedRoutes';
import { LazyLoadElement } from './LazyLoadElement';
import { LoggedOutElement } from './LoggedOutElement';
import { OnboardingRoutes } from './OnboardingRoutes';
import { ProfileRoutes } from './ProfileRoutes';
import ProtectRoute from './ProtectRoute';
import { RootErrorElement } from './RootErrorElement';
import { StaffRoutes } from './StaffRoutes';
import { WorkspaceRoutes } from './WorkspaceRoutes';

const appRoutes: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    /**
     * Global splat normalisation
     * https://reactrouter.com/6.30.3/hooks/use-resolved-path#splat-paths
     */
    loader: ({ request }) => {
      const url = new URL(request.url);

      // Redirect if it's not the root and ends with a slash
      if (url.pathname !== '/' && url.pathname.endsWith('/')) {
        return redirect(url.pathname.slice(0, -1) + url.search);
      }

      return null;
    },
    children: [
      {
        index: true,
        element: (
          <MigrationSwitch
            featureFlags={MSR3_HOME_FLAGS}
            newComponent={
              <ProtectRoute
                permissionRequired={[PermissionEnum.ViewWorkspace]}
                onAuthorised={() => {
                  prefetchWorkspaces();
                }}
              >
                <MSR3BaseLayout>
                  <WorkspaceRedirect />
                </MSR3BaseLayout>
              </ProtectRoute>
            }
            oldComponent={<NavigateWithParams replace to="/dashboard" />}
          />
        ),
      },
      {
        path: 'admin',
        children: AdminRoutes,
      },
      {
        path: 'dashboard',
        element: (
          <MigrationSwitch
            featureFlags={MSR3_HOME_FLAGS}
            newComponent={<NavigateWithParams replace to="/" />}
            oldComponent={
              <WorkspaceRedirect>
                <ProtectRoute
                  onAuthorised={() => {
                    prefetchWorkspaces();
                  }}
                >
                  <DashboardLayout>
                    <Dashboard />
                  </DashboardLayout>
                </ProtectRoute>
              </WorkspaceRedirect>
            }
          />
        ),
      },
      {
        path: 'invitation',
        element: LazyLoadElement(
          lazy(() => import('../pages/Invitation/Invitation')),
        ),
      },
      {
        path: 'invitation/first-password',
        element: LazyLoadElement(
          lazy(() => import('../pages/Invitation/FirstPasswordScreen')),
        ),
        handle: { skipWorkspaceSync: true },
      },
      {
        path: 'set-password',
        element: configuration.COGNITO_ENABLED ? (
          LazyLoadElement(lazy(() => import('../pages/Auth/SetPasswordPage')))
        ) : (
          <NavigateWithParams to="/dashboard" />
        ),
        handle: { skipWorkspaceSync: true },
      },
      {
        path: 'set-password/choose',
        element: LazyLoadElement(
          lazy(() => import('../pages/Invitation/FirstPasswordScreen')),
        ),
        handle: { skipWorkspaceSync: true },
      },
      {
        path: 'staff/accept-invitation',
        element: LazyLoadElement(
          lazy(() => import('../pages/Staff/StaffAcceptInvitation')),
        ),
        handle: { skipWorkspaceSync: true },
      },
      {
        path: 'login',
        element: configuration.COGNITO_ENABLED ? (
          LazyLoadElement(lazy(() => import('../pages/Auth/PreLoginPage')))
        ) : (
          <NavigateWithParams to="/dashboard" />
        ),
      },
      {
        path: 'auth/mfa-setup',
        element: LazyLoadElement(
          lazy(() => import('../pages/Auth/StandaloneMfaSetupPage')),
        ),
        handle: { skipWorkspaceSync: true },
      },
      {
        path: 'auth/mfa-reset',
        element: LazyLoadElement(
          lazy(() => import('../pages/Auth/StandaloneMfaResetPage')),
        ),
        handle: { skipWorkspaceSync: true },
      },
      {
        path: 'login-callback',
        element: LazyLoadElement(
          lazy(() => import('../components/auth/LoginCallback')),
        ),
        handle: { skipWorkspaceSync: true },
      },
      {
        path: 'logout',
        element: LazyLoadElement(
          lazy(() => import('../components/auth/LogoutCallback')),
        ),
      },
      {
        path: 'logged-out',
        element: <LoggedOutElement />,
        children: [
          {
            path: '',
            element: LazyLoadElement(
              lazy(() => import('../components/auth/LoggedOut')),
            ),
          },
        ],
      },
      {
        path: 'onboarding/:workspaceId',
        children: OnboardingRoutes,
      },
      {
        path: 'profile',
        children: ProfileRoutes,
      },
      {
        path: 'workspace',
        element: (
          <MigrationSwitch
            featureFlags={MSR3_HOME_FLAGS}
            newComponent={
              <ProtectRoute
                permissionRequired={[PermissionEnum.ViewWorkspace]}
                onAuthorised={() => {
                  prefetchWorkspaces();
                }}
              >
                <MSR3BaseLayout>
                  <WorkspaceRedirect />
                </MSR3BaseLayout>
              </ProtectRoute>
            }
            oldComponent={
              <DashboardLayout>
                <ErrorPage notFound />
              </DashboardLayout>
            }
          />
        ),
        loader: () => null,
      },
      {
        path: 'workspace/:workspaceId',
        loader: ({ params }) => {
          const { workspaceId } = params;

          prefetchWorkspace(workspaceId ?? '');

          return {};
        },
        children: WorkspaceRoutes,
      },
      {
        path: 'embed',
        children: EmbedRoutes,
      },
      {
        path: 'staff',
        children: StaffRoutes,
      },
      {
        path: 'terms',
        element: LazyLoadElement(
          lazy(() => import('../pages/TermsAndConditions/TermsAndConditions')),
        ),
      },
      {
        path: '*',
        element: (
          <DashboardLayout>
            <ErrorPage notFound />
          </DashboardLayout>
        ),
      },
    ],
    errorElement: <RootErrorElement />,
  },
];

export const AppRouter = createBrowserRouter(appRoutes);
export { appRoutes };
